import React, { useState, useRef } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import AuthLayout from '../components/AuthLayout';
import UserTypeChoice from '../components/UserTypeChoice';
import { USER_TYPES } from '../constants/designer';
import { setUserType } from '../firebase/userService';
import { takeReturnTo } from '../utils/returnTo';
import './AccountType.css';

// ── CUSTOMER OR DESIGNER ─────────────────────────────────────────────────────
// The one question the registration form asks that OAuth cannot. Signing in
// with Google skips the form entirely, so nobody was asked — and the account
// was created as a customer by default, with `userTypePending` marking that as
// an assumption rather than an answer.
//
// A PAGE, NOT A DIALOG. This replaces a modal that was portalled over whatever
// screen happened to be open. The question is a step in signing up, not an
// interruption of something else, and a page can say so: it gets the same
// branded layout as /login and /register, it has a URL that survives a reload,
// and it cannot be mistaken for something dismissible.
//
// WHO ARRIVES HERE. Two routes in, deliberately:
//   · the Google button on /login and /register, which knows it just created an
//     account and comes straight here — no flash of a dashboard first;
//   · ProtectedRoute, which sends ANY signed-in user still carrying the flag.
//     That is the one that matters: it covers a sign-up abandoned halfway, a
//     closed tab, and the guest flow, where registering happens inside a modal
//     that navigates somewhere of its own.
//
// AND WHERE THEY GO NEXT. A designer's or a shop's public profile is what other
// people find them by and it is empty at this point, so they are handed straight
// to the form that fills it in — a shop in particular is unfindable until it has
// said what it sells. A customer goes back to whatever they were trying to reach —
// ProtectedRoute passes it in `state.from`, which is what keeps the guest flow
// intact: they were on their way into a newly created project, and they land in
// it rather than on the dashboard.
export default function AccountType() {
  const { user, profile, refreshProfile } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-selected, so Continue is always a valid action. Customer is the safer
  // default of the two — it is also what the account already is.
  const [picked, setPicked] = useState(USER_TYPES.CUSTOMER);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Set the moment the choice is saved. Without it the "already answered" guard
  // below would fire on the render after `refreshProfile`, racing this page's
  // own navigation to decide where the user ends up.
  const answered = useRef(false);

  // WHERE THEY WERE HEADED. `state.from` when something in-app handed it over,
  // and otherwise the path the sign-up dialog on a public profile wrote down —
  // this page is a step in creating an account and nothing else, so consuming
  // that here cannot hijack an ordinary visit to anywhere.
  //
  // Once, in a lazy initialiser, because reading it consumes it. `useState`
  // rather than a ref so the two early returns below still see a computed
  // value: they run after the hooks, not before them.
  const [back] = useState(() => location.state?.from?.pathname || takeReturnTo() || '/dashboard');

  // Not signed in at all: ProtectedRoute normally catches this, so it is here
  // only for a direct hit on the URL.
  if (!user) return <Navigate to="/login" replace />;

  // ALREADY ANSWERED — someone typing the URL, or coming back to it. `profile`
  // is null while it loads, and the check tolerates that on purpose: showing
  // the question for a moment is a far smaller cost than blocking the page on a
  // read, and the flag is what makes the choice idempotent anyway.
  if (profile && !profile.userTypePending && !answered.current) {
    return <Navigate to={back} replace />;
  }

  const choose = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await setUserType(user.uid, picked);
      answered.current = true;
      // Before navigating, so the page being landed on already knows the type —
      // Settings renders a different form for each.
      await refreshProfile();
      // EVERY role lands on Settings now, which renders the right form for
      // whichever was chosen — the designer's and the shop's public profiles,
      // and for a customer the picture, name and location.
      //
      // A customer used to go straight on to wherever they were headed, on the
      // reasoning that they have no public profile to fill in. That left the
      // one-step sign-ins with nothing: Google supplies a name and picture but
      // never a location, and a phone credential carries NO name at all, so a
      // phone customer reached the app as an unnamed account and showed up as
      // "—" on every comment they wrote.
      //
      // WHERE THEY WERE GOING IS NOT DISCARDED. It rides along in `from`, and
      // Settings returns them to it once they save — which is what keeps the
      // guest flow intact: they were on their way into a project they had just
      // created, and they still land in it.
      const publishes = picked === USER_TYPES.DESIGNER || picked === USER_TYPES.SHOP;
      navigate('/settings', {
        replace: true,
        state: publishes ? undefined : { from: { pathname: back } },
      });
    } catch {
      setError(t('atg_save_failed'));
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="atp">
        <div className="af-header">
          <h2 className="af-title">{t('atg_welcome')}</h2>
          <p className="af-subtitle">{t('atg_sub')}</p>
        </div>

        {/* THE SAME COMPONENT THE REGISTRATION FORM USES. Two copies of this
            choice would be two things to keep in step — same labels, same
            icons, same wording of what each account unlocks. */}
        <UserTypeChoice value={picked} onChange={setPicked} disabled={busy} />

        <button type="button" className="atp-go" onClick={choose} disabled={busy}>
          {busy ? '…' : t('auth_continue')}
        </button>

        {error && <p className="atp-err">{error}</p>}
        <p className="atp-note">{t('atg_change_later')}</p>
      </div>
    </AuthLayout>
  );
}
